// Camada de query da visão geral e dos alertas do admin (`spec/17-frontend.md` §8.2,
// WEB-20). Os tipos saem inteiros de `src/api/types/admin.d.ts` (§5.2).
//
// CONTRATO DE QUERY KEY (`app/live-events.ts`): domínio primeiro. O mapa de invalidação
// de §4.5 **não alcança** estas keys, e isso é decisão, não esquecimento: `useLiveEvents()`
// só é montado no shell do seller (`src/routes/app.tsx`), `GET /events` é o feed do
// seller e o catálogo não tem evento de admin. A frescura destas telas vem do
// `refetchOnWindowFocus` do `QueryClient` mais o polling curto de §4.5 (`ADMIN_POLL_MS`),
// que ali é fallback obrigatório, não enfeite. Registrado em "Questões abertas".
//
// `mode` não entra na key porque o admin não tem toggle global (§4.4/§13 q4): as duas
// rotas ficam no `live` default da API. Quando WEB-21..31 trouxerem o filtro por tela,
// ele entra DEPOIS do domínio, como no seller.

import { queryOptions } from '@tanstack/react-query';
import { api } from '../client.ts';
import type { paths } from '../types/admin';

/** Corpo `200` de um `GET` do documento gerado, nenhum tipo de resposta à mão (§5.2). */
type Get200<P extends keyof paths> = paths[P] extends {
  get: { responses: { 200: { content: { 'application/json': infer T } } } };
}
  ? T
  : never;

export type AdminOverview = Get200<'/admin/v1/overview'>;
export type AdminAlertsPage = Get200<'/admin/v1/system/alerts'>;
export type AdminAlert = AdminAlertsPage['data'][number];
export type AlertSeverity = AdminAlert['severity'];

/** Os três presets que `GET /admin/v1/overview` aceita (`spec/12` §4.4). */
export const OVERVIEW_PERIODS = ['today', '7d', '30d'] as const;
export type OverviewPeriod = (typeof OVERVIEW_PERIODS)[number];

/**
 * Fallback de §4.5 sem SSE nesta superfície. 30 s é o teto da spec; o servidor ainda
 * cacheia o overview por 15 s (`OVERVIEW_CACHE_TTL_MS`), então bater mais rápido só
 * gastaria request para receber o mesmo corpo.
 */
export const ADMIN_POLL_MS = 30_000;

export function adminOverviewQueryOptions(period: OverviewPeriod, region?: 'brl' | 'global') {
  return queryOptions({
    queryKey: ['admin-overview', period, region],
    queryFn: () => api<AdminOverview>('/admin/v1/overview', { query: { period, region } }),
    refetchInterval: ADMIN_POLL_MS,
  });
}

export function adminAlertsQueryOptions() {
  return queryOptions({
    queryKey: ['admin-alerts'],
    queryFn: () => api<AdminAlertsPage>('/admin/v1/system/alerts'),
    refetchInterval: ADMIN_POLL_MS,
  });
}

/** Keys que o ack invalida: o contador de alertas críticos também mora no overview. */
export const ALERT_ACK_INVALIDATES: string[][] = [['admin-alerts'], ['admin-overview']];

/**
 * Silencia por 24 h. A chave é o **nome** do alerta, não um id (`spec/17` §8.3), e um
 * nome pode cobrir várias linhas (`entity_key` por endpoint/seller): o ack vale para
 * todas. Resposta `204` sem corpo.
 */
export function ackAlertPath(name: string): string {
  return `/admin/v1/system/alerts/${encodeURIComponent(name)}/ack`;
}

export async function ackAlert(name: string): Promise<void> {
  await api<undefined>(ackAlertPath(name), { method: 'POST' });
}
